import { FC } from 'react';

import { useCms } from '@eneco/cms';
import RichText from '@eneco/components/RichText/RichText';
import { Button, Image, Stack } from '@eneco/sparky';
import { ProfileIcon } from '@eneco/sparky/icons';
import { signIn } from 'next-auth/react';

import { Header } from '../../components/Header/Header';
import { Layout } from '../../components/Layout/Layout';
import { useStepContent } from '../../hooks';
import { readSectionField } from '../../utils/readSectionField';

interface Props {
  /** Shows the step loading, as a flow does while it checks the session. */
  isLoading?: boolean;
}

/**
 * What a login step shows: its title, text and image, and the button that sends the visitor to log in, from the
 * step's `LogInStep` content on either CMS.
 *
 * Several flows have a login step of their own, and they differ only in what happens once the visitor is logged in.
 * Each keeps that part, and the reading of the content lives here once.
 *
 * The text sits in a field named like its section, `content`, which stays inside that section on the Sitecore lane,
 * hence `readSectionField`.
 */
export const LogInStepView: FC<Props> = ({ isLoading = false }) => {
  const content = useStepContent('LogInStep');
  const { adapter } = useCms();
  const text = readSectionField<string>(content, 'content', 'content');
  const image = adapter.toImage(content?.image);

  return (
    <Layout.Content variant="A" isLoading={isLoading}>
      <Header>
        <Header.Title>{content?.title}</Header.Title>
        {text && (
          <Header.Text as="div">
            <RichText html={text} />
          </Header.Text>
        )}
      </Header>
      {image?.src ? (
        <Image src={image.src} alt={image.alt ?? ''} aspectRatio="16/9" height="auto" objectFit="cover" />
      ) : null}
      <Stack direction={{ initial: 'column', md: 'row' }}>
        <Button onClick={() => signIn('okta')}>
          <ProfileIcon /> {content?.logInButtonText}
        </Button>
      </Stack>
    </Layout.Content>
  );
};
